import PropTypes from 'prop-types';
import segmentize from 'segmentize';
import InlineSvg from 'Shared/components/inline-svg';
import { useTranslation } from 'Shared/util/custom-hooks';
import styles from './data-table-paging.css';


const handlePageClick = (page, handlePaging, action, numberOfPages, currentPage) => {
  let nextPage = page;
  if (!nextPage) {
    switch (action) {
      case 'first':
        nextPage = 1;
        break;
      case 'previous':
        nextPage = currentPage - 1 < 1 ? 1 : currentPage - 1;
        break;
      case 'next':
        nextPage = currentPage + 1 > numberOfPages ? numberOfPages : currentPage + 1;
        break;
      case 'last':
        nextPage = numberOfPages;
        break;
      default:
        break;
    }
  }
  handlePaging(nextPage);
};

const renderPageButtons = (pageNumbers, handlePaging, isCurrent, addEllipsis) => (
  pageNumbers.map((p) => (
    <button
      key={p}
      type="button"
      onClick={() => (!isCurrent ? handlePageClick(p, handlePaging) : {})}
      className={`${styles.itemBox} ${isCurrent ? styles.active : null}`}
    >
      {addEllipsis ? `...${p}` : p}
    </button>
  ))
);

const showFirstpage = (currentPage) => (currentPage === 1 || currentPage === 2);
const showThirdPage = (currentPage, numberOfPages) => (currentPage === 1 && numberOfPages > 3);
const showThirdLastPage = (currentPage, numberOfPages) => (numberOfPages > 2 && currentPage === numberOfPages);
const showLastPageMobileMode = (currentPage, numberOfPages) => (currentPage === 1 || currentPage === numberOfPages || currentPage === numberOfPages - 1);
const showLastPageWithEllipsis = (currentPage, numberOfPages) => (numberOfPages - currentPage > 2);

const renderPages = (currentPage, numberOfPages, handlePaging, isMobileMode) => {
  if (numberOfPages === 0) return [];

  const segments = segmentize({
    page: currentPage,
    pages: numberOfPages,
    beginPages: 1,
    endPages: 1,
    sidePages: 1,
  });

  const pages = [].concat.apply([], [
    showFirstpage(currentPage) ? renderPageButtons(segments.beginPages, handlePaging) : [],
    showThirdLastPage(currentPage, numberOfPages) ? renderPageButtons([segments.centerPage[0] - 2], handlePaging) : [],
    renderPageButtons(segments.previousPages, handlePaging),
    renderPageButtons(segments.centerPage, handlePaging, true),
    renderPageButtons(segments.nextPages, handlePaging),
    showThirdPage(currentPage, numberOfPages) ? renderPageButtons([3], handlePaging) : [],
    // eslint-disable-next-line no-nested-ternary
    (!isMobileMode || showLastPageMobileMode(currentPage, numberOfPages))
      ? showLastPageWithEllipsis(currentPage, numberOfPages)
        ? renderPageButtons(segments.endPages, handlePaging, false, true)
        : renderPageButtons(segments.endPages, handlePaging)
      : [],
  ]);

  return pages;
};


const DataTablePaging = ({
  currentPage,
  pageSize,
  rowCount,
  handlePaging,
  handleShowAllRows,
  isMobileMode,
  showAllRows,
}) => {
  const t = useTranslation(x => x.dataTable);

  const handleShowAllRowsClick = () => {
    handleShowAllRows(!showAllRows);
  };

  const numberOfPages = Math.ceil(rowCount / pageSize);
  if (numberOfPages === 1) { return null; }

  const showAllText = (showAllRows ? t.showFewerRows : t.showAllRows);

  return (
    <div className={styles.container}>
      <button
        type="button"
        onClick={handleShowAllRowsClick}
        className={styles.itemBox}
      >
        {showAllText}
      </button>

      {!showAllRows && (
        <div className={styles.pagingContainer}>
          {currentPage > 1 && (
          <>
            <button
              type="button"
              onClick={() => handlePageClick(null, handlePaging, 'first', numberOfPages, currentPage)}
              className={styles.itemBox}
            >
              <InlineSvg src="/caret-left-end.svg" />
            </button>
            <button
              type="button"
              onClick={() => handlePageClick(null, handlePaging, 'previous', numberOfPages, currentPage)}
              className={styles.itemBox}
            >
              <InlineSvg src="/caret-left.svg" />
            </button>
          </>
          )}
          {renderPages(currentPage, numberOfPages, handlePaging, isMobileMode)}
          {currentPage < numberOfPages && (
          <>
            <button
              type="button"
              onClick={() => handlePageClick(null, handlePaging, 'next', numberOfPages, currentPage)}
              className={styles.itemBox}
            >
              <InlineSvg src="/caret-right.svg" />
            </button>
            <button
              type="button"
              onClick={() => handlePageClick(null, handlePaging, 'last', numberOfPages, currentPage)}
              className={styles.itemBox}
            >
              <InlineSvg src="/caret-right-end.svg" />
            </button>
          </>
          )}
        </div>
      )}
    </div>
  );
};

DataTablePaging.propTypes = {
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  handlePaging: PropTypes.func.isRequired,
  handleShowAllRows: PropTypes.func.isRequired,
  isMobileMode: PropTypes.bool.isRequired,
  showAllRows: PropTypes.bool.isRequired,
};

export default DataTablePaging;
